import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ClipboardCheck, Timer, ShieldCheck, Hourglass, MailCheck, MailX, Send, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const QUIZ_DURATION_SECONDS = 20 * 60; // 20 minuti

/**
 * AssessmentQuiz — flusso operatore:
 *  1) Se non ha autorizzazione HR → schermata di richiesta
 *  2) Se pending → schermata di attesa
 *  3) Se approved → quiz con timer sticky + progress bar sticky (20 min)
 *  4) Dopo submit → schermata "Comando in valutazione" (NO grado visibile)
 *  5) Se used o rejected → schermata riepilogativa
 */
export default function AssessmentQuiz() {
  const [phase, setPhase] = useState("loading"); // loading | no_auth | pending | approved | submitted | used | rejected
  const [auth, setAuth] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [motivazione, setMotivazione] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(QUIZ_DURATION_SECONDS);
  const submittedRef = useRef(false);
  const questionsRef = useRef([]);
  const answersRef = useRef({});
  answersRef.current = answers;
  questionsRef.current = questions;

  const refreshAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/assessment/auth/my");
      const item = data?.item || null;
      setAuth(item);
      if (!item) return "no_auth";
      if (item.status === "pending") return "pending";
      if (item.status === "rejected") return "rejected";
      if (item.status === "used") return "used";
      if (item.status === "approved") return "approved";
      return "no_auth";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento autorizzazione");
      return "no_auth";
    }
  }, []);

  const loadQuestions = useCallback(async () => {
    const { data } = await api.get("/assessment/questions");
    setQuestions(data?.items || []);
    setAnswers({});
    setSecondsLeft(QUIZ_DURATION_SECONDS);
    submittedRef.current = false;
  }, []);

  const bootstrap = useCallback(async () => {
    setPhase("loading");
    const next = await refreshAuth();
    if (next === "approved") {
      try {
        await loadQuestions();
        setPhase("approved");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento quiz");
        setPhase("no_auth");
      }
    } else {
      setPhase(next);
    }
  }, [refreshAuth, loadQuestions]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const requestAuth = async () => {
    setRequesting(true);
    try {
      await api.post("/assessment/auth/request", { motivazione: motivazione.trim() });
      toast.success("Richiesta inviata alle Risorse Umane.");
      setMotivazione("");
      await bootstrap();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setRequesting(false);
    }
  };

  const submit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const qs = questionsRef.current;
      const ans = answersRef.current;
      const payload = { answers: qs.map((q) => (ans[q.id] ?? -1)) };
      await api.post("/assessment/submit", payload);
      setPhase("submitted");
      if (auto) toast.info("Tempo scaduto: quiz inviato automaticamente.");
      await refreshAuth();
    } catch (e) {
      submittedRef.current = false;
      toast.error(e?.response?.data?.detail || "Invio fallito");
    } finally {
      setSubmitting(false);
    }
  }, [refreshAuth]);

  // Timer countdown — solo in fase quiz
  useEffect(() => {
    if (phase !== "approved") return;
    if (secondsLeft <= 0) { submit(true); return; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, secondsLeft, submit]);

  // -------- Render fasi --------
  if (phase === "loading") {
    return <div className="text-center py-16 text-[#B8A269]"><Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" /> Caricamento…</div>;
  }

  if (phase === "no_auth" || phase === "rejected") {
    return (
      <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="assessment-request-auth">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl border border-sheriff-gold/40 bg-sheriff-gold/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-6 w-6 text-sheriff-gold" />
          </div>
          <div>
            <div className="uppercase-label text-sheriff-gold text-[10px]">Autorizzazione richiesta</div>
            <h3 className="font-heading font-black text-xl text-[#F5E6B8]">Quiz Attitudinale · Valutazione Grado</h3>
          </div>
        </div>
        {phase === "rejected" && auth && (
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4">
            <div className="flex items-center gap-2 mb-1 text-rose-200 text-sm font-bold uppercase-label"><MailX className="h-4 w-4" /> Richiesta precedente rifiutata</div>
            {auth.hr_note && <p className="text-xs text-rose-100/80 mt-1 whitespace-pre-wrap">{auth.hr_note}</p>}
            <p className="text-[10px] text-rose-200/60 mt-1 font-mono">
              {auth.decided_at && new Date(auth.decided_at).toLocaleString("it-IT")}
              {auth.decided_by_name ? ` · ${auth.decided_by_name}` : ""}
            </p>
          </div>
        )}
        <p className="text-sm text-[#D4C480] leading-relaxed">
          Per sostenere il <b>Quiz Attitudinale</b> (40 domande, 20 minuti, tratte dal Manuale dell&apos;Operatore) devi ottenere l&apos;autorizzazione dalle <b>Risorse Umane</b>.
          Il risultato del quiz sarà valutato dal Comando; a te verrà mostrata solo la conferma di completamento.
        </p>
        <div>
          <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 block">Motivazione (opzionale)</label>
          <Textarea
            rows={3}
            value={motivazione}
            onChange={(e) => setMotivazione(e.target.value)}
            placeholder="Perché richiedi la valutazione? (es. desidero conoscere il mio grado potenziale)"
            className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-[#8B7B49]"
            data-testid="assessment-request-motivation"
          />
        </div>
        <div className="flex justify-end">
          <Button
            onClick={requestAuth}
            disabled={requesting}
            data-testid="assessment-request-submit"
            className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold"
          >
            {requesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Invia richiesta a HR
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "pending") {
    return (
      <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-amber-500/40 rounded-2xl p-8 text-center space-y-3" data-testid="assessment-pending-auth">
        <div className="h-14 w-14 mx-auto rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
          <Hourglass className="h-7 w-7 text-amber-300 animate-pulse" />
        </div>
        <div className="uppercase-label text-amber-300">Richiesta in attesa</div>
        <h3 className="font-heading font-black text-2xl text-[#F5E6B8]">Le Risorse Umane stanno valutando la tua richiesta</h3>
        <p className="text-sm text-[#D4C480] max-w-md mx-auto">
          Riceverai il via libera non appena HR approverà la tua richiesta. Torna qui per iniziare il quiz.
        </p>
        {auth?.motivazione && (
          <div className="text-xs text-[#B8A269] italic max-w-md mx-auto">&ldquo;{auth.motivazione}&rdquo;</div>
        )}
        <div className="text-[10px] text-[#8B7B49] font-mono pt-2">
          Inviata: {auth?.requested_at ? new Date(auth.requested_at).toLocaleString("it-IT") : "—"}
        </div>
      </div>
    );
  }

  if (phase === "submitted" || phase === "used") {
    return (
      <div className="relative overflow-hidden bg-gradient-to-br from-[#143830] via-[#0F2620] to-[#0A1F18] border-2 border-sheriff-gold/40 rounded-2xl p-8 text-center space-y-4" data-testid="assessment-under-review">
        <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-sheriff-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-sheriff-gold/10 blur-3xl pointer-events-none" />
        <div className="h-14 w-14 mx-auto rounded-full bg-sheriff-gold/15 border border-sheriff-gold/50 flex items-center justify-center relative">
          <Sparkles className="h-7 w-7 text-sheriff-gold animate-pulse" />
        </div>
        <div className="uppercase-label text-sheriff-gold relative">Valutazione completata</div>
        <h3 className="font-heading font-black text-2xl sm:text-3xl text-[#F5E6B8] tracking-tighter relative">
          Il Comando sta valutando il tuo esito
        </h3>
        <p className="text-sm text-[#D4C480] max-w-md mx-auto relative">
          Le tue risposte sono state trasmesse alle <b>Risorse Umane</b>. Il grado potenziale sarà visionato dal Comando e comunicato tramite i canali ufficiali del Dipartimento.
        </p>
        <div className="inline-flex items-center gap-2 text-[10px] text-sheriff-gold/70 font-mono uppercase tracking-[0.15em] relative">
          <MailCheck className="h-3.5 w-3.5" /> Verbale inoltrato al Personnel Administration Bureau
        </div>
      </div>
    );
  }

  // phase === "approved"
  const answeredCount = Object.keys(answers).length;
  const progressPct = (answeredCount / questions.length) * 100;
  const timePct = (secondsLeft / QUIZ_DURATION_SECONDS) * 100;
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const timeLow = secondsLeft <= 60;
  const timeMid = secondsLeft <= 5 * 60;

  return (
    <div className="space-y-4" data-testid="assessment-quiz">
      <div className="sticky top-0 z-30 -mx-2 sm:mx-0 backdrop-blur-md" data-testid="assessment-sticky-header">
        <div className="bg-[#0A1F18]/95 border border-sheriff-gold/40 rounded-2xl p-4 shadow-2xl shadow-black/50 space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Timer className={`h-5 w-5 ${timeLow ? "text-rose-400 animate-pulse" : timeMid ? "text-amber-300" : "text-sheriff-gold"}`} />
              <span className="uppercase-label text-sheriff-gold/80 text-[10px]">Tempo rimanente</span>
              <span className={`font-mono font-black text-xl ${timeLow ? "text-rose-300" : timeMid ? "text-amber-300" : "text-sheriff-gold"}`} data-testid="assessment-timer">
                {mm}:{ss}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="uppercase-label text-sheriff-gold/80 text-[10px]">Progresso</span>
              <span className="font-mono font-bold text-sheriff-gold" data-testid="assessment-progress">
                {answeredCount}/{questions.length}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="h-1.5 bg-[#143830] rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-1000 ${timeLow ? "bg-rose-400" : timeMid ? "bg-amber-300" : "bg-sheriff-gold"}`} style={{ width: `${timePct}%` }} data-testid="assessment-time-bar" />
            </div>
            <div className="h-1.5 bg-[#143830] rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400 transition-all" style={{ width: `${progressPct}%` }} data-testid="assessment-progress-bar" />
            </div>
          </div>
        </div>
      </div>

      {questions.map((q, idx) => (
        <div key={q.id} className="bg-[#0F2620] border border-sheriff-gold/25 rounded-xl p-5" data-testid={`assessment-q-${idx}`}>
          <div className="uppercase-label text-sheriff-gold/70 text-[10px] mb-2">Domanda {idx + 1} di {questions.length}</div>
          <div className="text-[#F5E6B8] mb-3 font-medium">{q.text}</div>
          <div className="space-y-2">
            {q.options.map((opt, i) => {
              const selected = answers[q.id] === i;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: i }))}
                  data-testid={`assessment-q-${idx}-opt-${i}`}
                  className={`w-full text-left px-4 py-2.5 rounded-lg border transition-all ${
                    selected
                      ? "border-sheriff-gold bg-sheriff-gold/10 text-[#F5E6B8]"
                      : "border-sheriff-gold/20 bg-[#0A1F18]/50 text-[#D4C480] hover:border-sheriff-gold/50"
                  }`}
                >
                  <span className="uppercase-label text-xs mr-3 opacity-70">{String.fromCharCode(65 + i)}</span>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="pt-4 pb-2 flex justify-end">
        <Button
          onClick={() => submit(false)}
          disabled={submitting}
          data-testid="assessment-submit"
          className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase-label px-8 shadow-xl disabled:opacity-40"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Invia al Comando
        </Button>
      </div>
    </div>
  );
}
